// ============================================================================
// DEFAULT PROPERTY — auto-select when the tenant has exactly one
// ============================================================================
// Most CRS tenants run a single property. Making that user pick it from a
// dropdown before the Booking Worksheet's strip, Day View or Calendar will show
// anything is pure friction — and worse, those views render a "select a
// property" dead end that looks like an empty screen.
//
// When exactly ONE active property exists it is returned as the default. With
// two or more, nothing is returned: the choice is then genuinely the user's, and
// silently picking one of several would scope a screen to a property the user
// never chose.
//
// Cheap by design: pageSize 1, and the decision is made from
// `pagination.totalItems`, so this never fetches a property list it does not use.

import { useState, useEffect } from 'react';
import { propertyService } from 'api/services/propertyService';

interface UseDefaultPropertyResult {
  /** The single active property's id, or '' when there are 0 or 2+. */
  defaultPropertyId: string;
  /** True only when exactly one active property exists. */
  isSingleProperty: boolean;
  /** True until the lookup settles — callers should not seed state before then. */
  loading: boolean;
}

export const useDefaultProperty = (): UseDefaultPropertyResult => {
  const [defaultPropertyId, setDefaultPropertyId] = useState('');
  const [isSingleProperty, setIsSingleProperty] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    propertyService
      .getAll({ page: 1, pageSize: 1, includeInactive: false })
      .then((res) => {
        if (cancelled) return;
        const total = res.pagination?.totalItems ?? 0;
        const only = res.data?.[0];
        if (total === 1 && only?.id) {
          setDefaultPropertyId(only.id);
          setIsSingleProperty(true);
        } else {
          setDefaultPropertyId('');
          setIsSingleProperty(false);
        }
      })
      .catch(() => {
        // Non-fatal: the screen still works, the user just picks a property.
        if (cancelled) return;
        setDefaultPropertyId('');
        setIsSingleProperty(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { defaultPropertyId, isSingleProperty, loading };
};

export default useDefaultProperty;
